import * as React from 'react';
import { GroupServiceGetResponse, RateReliefCalculatorServiceResponse } from '../../../services/api';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import ModalButtons from '../../ui/modals/ModalButtons';
import { Dialog } from 'primereact/dialog';
import { APIStatus, GroupStatus } from '../../../utils/types';
import { acceptTier2Request, declineTier2Request } from '../../../store/group';
import { Message } from 'primereact/message';
import FileUploader from '../../uploads/FileUploader';
import { flattenTimePeriodDate } from '../../../utils/date';
import MinMaxCompRates from './MinMaxCompRates';

export default function Tier2ResponseForm({ group, rates, onComplete }: { group: GroupServiceGetResponse, rates: RateReliefCalculatorServiceResponse , onComplete: () => void }) {
  const [newRrAmount, setNewRrAmount] = React.useState<number|null>(null);
  const [note, setNote] = React.useState<string>('');
  const [files, setFiles] = React.useState<File[]>([]);

  const [showingDeclineModal, setShowingDeclineModal] = React.useState<boolean>(false);
  const [showingAcceptModal, setShowingAcceptModal] = React.useState<boolean>(false);
  const [formApiState, setFormApiState] = React.useState<APIStatus|null>(null);

  function appendFile(files: File[]) {
    setFiles(files);
  }

  async function submitAcceptanceForm() {
    setFormApiState(APIStatus.LOADING);

    try {
      await acceptTier2Request({
        note,
        newRateReliefAmount: newRrAmount! / 100,
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
        file: files[0],
      });

      setFormApiState(null);
      setNote('');
      setNewRrAmount(null);
      setShowingAcceptModal(false);
      onComplete();
    } catch {
      setFormApiState(APIStatus.ERROR);
    }
  }

  async function submitRejectionForm() {
    setFormApiState(APIStatus.LOADING);

    try {
      await declineTier2Request({
        note,
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
        file: files[0],
      });

      setFormApiState(null);
      setNote('');
      setNewRrAmount(null);
      setShowingDeclineModal(false);
      onComplete();
    } catch {
      setFormApiState(APIStatus.ERROR);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4" data-tier-2-response-form>
        <div className="flex flex-col gap-1">
          <label className="font-bold" htmlFor="new_rr_amount">New rate relief amount (%)</label>
          <InputNumber value={newRrAmount} onChange={(e) => setNewRrAmount(e.value)} inputId="new_rr_amount" placeholder="Enter percentage (e.g. 10%)" />
        </div>
        {group.requestStatus !== GroupStatus.TIER2_REQUESTED && (
          <div className="flex flex-col gap-1">
            <FileUploader files={files} onAddFile={appendFile} />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className="font-bold" htmlFor="note">Supporting notes</label>
          <InputTextarea value={note} onChange={(e) => setNote(e.target.value)} id="note" placeholder="Provide reasoning for your decision" rows={5} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Button disabled={!note} rounded severity="danger" label="decline request" onClick={() => setShowingDeclineModal(true)} />
          <Button disabled={!note.length || !newRrAmount} rounded label="approve relief" onClick={() => setShowingAcceptModal(true)} />
        </div>
      </div>  

      <div className="mt-4">
        <MinMaxCompRates rates={rates} />
      </div>

      <Dialog
        visible={showingDeclineModal}
        draggable={false}
        header="Decline relief request?"
        onHide={() => setShowingDeclineModal(false)}
        footer={
          <ModalButtons
            isLoading={formApiState === APIStatus.LOADING}
            saveLabel="yes, decline"
            submitButtonSeverity="danger"
            isFormValid={true}
            onCancel={() => setShowingDeclineModal(false)}
            onSave={submitRejectionForm}
          />
        }
      >
        <p>Are you sure? <strong>{group.policyDetailModel?.policyNumber} {group.policyDetailModel?.groupName}</strong> won't receive tier 2 relief if you decline.</p>

        {formApiState === APIStatus.ERROR && (
          <div className="flex flex-col mt-4">
            <Message severity="error" text="There was an error submitting your response, please try again" />
          </div>
        )}
      </Dialog>

      <Dialog
        visible={showingAcceptModal}
        draggable={false}
        header="Approve relief request?"
        onHide={() => setShowingAcceptModal(false)}
        footer={
          <ModalButtons
            isLoading={formApiState === APIStatus.LOADING}
            saveLabel="yes, approve"
            isFormValid={true}
            onCancel={() => setShowingAcceptModal(false)}
            onSave={submitAcceptanceForm}
          />
        }
      >
        <p>This will approve the tier 2 relief request from <strong>{group?.policyDetailModel?.groupName} ({group?.policyDetailModel?.policyNumber})</strong>.</p>

        {formApiState === APIStatus.ERROR && (
          <div className="flex flex-col mt-4">
            <Message severity="error" text="There was an error submitting your response, please try again" />
          </div>
        )}
      </Dialog>
    </>
  )
}